import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { AceGutachtenAceGutachtenEnumsAceGutachtenStatuscode, DocumentDisplayModel, GlobalEnumsAceBerechnungsmethode, GlobalEnumsAceRechtsform, GutachtenDisplayModel, GutachtenDisplayModelFormGroup } from '@/api/model-index';
import { I18nService } from '@/core/services/i18nService';
import { LoadingAnimationService } from "@/core/services/loading-animation.service";
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Constants } from "@/constants";
import { EnumValue } from "@/core/models/enumvalue.model";
import { NumberFormatOptions } from "@progress/kendo-angular-intl";
import { GutachtenService } from "@/api/service-index";
import { PortalContextService } from "@/core/services/portal-context.service";
import { DocumentService } from "@/core/services/document.service";
import { ConfirmationDialogComponent } from "@/core/components/dialogs/confirmation-dialog/confirmation-dialog.component";
import { DialogRef, DialogService } from "@progress/kendo-angular-dialog";
import { StateService } from "@/core/services/state.service";
import { AnfrageNavigationFormComponent } from "@/core/components/anfrage-navigation-form/anfrage-navigation-form.component";

@Component({
    selector: 'review-project-overview',
    templateUrl: './review-project-overview.component.html',
    styleUrls: ['./review-project-overview.component.scss']
})
export class ReviewProjectOverviewComponent implements OnInit {
    protected currencyFormatOptions: NumberFormatOptions = Constants.CURRENCY_FORMAT_OPTIONS;
    protected submitReviewDialog: DialogRef;
    protected reviewDisabledDialog: DialogRef;

    @Input('form') reviewForm: GutachtenDisplayModelFormGroup;
    @Input() review: GutachtenDisplayModel;
    @Input() public attachmentsForm: UntypedFormGroup

    public get dateFormat(): string {
      return this.languageservice.GetCurrentShortDateFormat(true);
    };

    public get attachmentsFormControl(): UntypedFormControl {
      return <UntypedFormControl>this.attachmentsForm.controls["attachments"];
    }

    public financeDataDisplayForm: UntypedFormGroup = new UntypedFormGroup({
        gesamt: new UntypedFormControl({value: null}, []),
        foerdersumme: new UntypedFormControl({value: null}, []),
    });

    public get gesamtFormControl(): AbstractControl {
      return this.financeDataDisplayForm.controls["gesamt"];
    }

    public get foerdersummeFormControl(): AbstractControl {
      return this.financeDataDisplayForm.controls["foerdersumme"];
    }

    public get foerdersumme(): number {
      if (this.berechnungsmethode == GlobalEnumsAceBerechnungsmethode.Fordersumme) {
        let personalmittel: number = this.reviewForm.Anfrage.FinanceData.Personalmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Personalmittel.value : 0;
        let sachmittel: number = this.reviewForm.Anfrage.FinanceData.Sachmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Sachmittel.value : 0;
        let investitionsmittel: number = this.reviewForm.Anfrage.FinanceData.Investitionsmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Investitionsmittel.value : 0;
        return personalmittel + sachmittel + investitionsmittel;
      }
      else if (this.berechnungsmethode == GlobalEnumsAceBerechnungsmethode.Gesamtprojektsumme) {
        let eigenmittel: number = this.reviewForm.Anfrage.FinanceData.Eigenmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Eigenmittel.value : 0;
        let drittmittel: number = this.reviewForm.Anfrage.FinanceData.Drittmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Drittmittel.value : 0;
    
        return this.gesamt - (eigenmittel + drittmittel);
      }

      return 0;
    }
    
    public get gesamt(): number {
      if (this.berechnungsmethode == GlobalEnumsAceBerechnungsmethode.Fordersumme) {
        let eigenmittel: number = this.reviewForm.Anfrage.FinanceData.Eigenmittel.value != null 
          ? this.reviewForm.Anfrage.FinanceData.Eigenmittel.value : 0;
        let drittmittel: number = this.reviewForm.Anfrage.FinanceData.Drittmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Drittmittel.value : 0;
    
        return this.foerdersumme + eigenmittel + drittmittel;
      }
      else if (this.berechnungsmethode == GlobalEnumsAceBerechnungsmethode.Gesamtprojektsumme) {
        let personalmittel: number = this.reviewForm.Anfrage.FinanceData.Personalmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Personalmittel.value : 0;
        let sachmittel: number = this.reviewForm.Anfrage.FinanceData.Sachmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Sachmittel.value : 0;
        let investitionsmittel: number = this.reviewForm.Anfrage.FinanceData.Investitionsmittel.value != null ? this.reviewForm.Anfrage.FinanceData.Investitionsmittel.value : 0;
        return personalmittel + sachmittel + investitionsmittel;
      }
  
      return 0;
    }

    constructor(
        protected languageservice: I18nService, 
        protected loadingAnimationService: LoadingAnimationService,
        protected gutachtenService: GutachtenService,
        protected portalContextService: PortalContextService,
        protected documentService: DocumentService,
        protected dialogService: DialogService) {

    }

    ngOnInit(): void {
      this.reviewForm.Anfrage.ProjectData.ProjektStart.patchValue(new Date(this.review.anfrage?.projectData?.projektStart || ""));
      this.reviewForm.Anfrage.ProjectData.ProjektEnd.patchValue(new Date(this.review.anfrage?.projectData?.projektEnd || ""));

      if ((this.review.ausschreibung?.fristFuerBegutachtung != null && Date.parse(this.review.ausschreibung?.fristFuerBegutachtung) < Date.now())
        && this.review.statuscode != AceGutachtenAceGutachtenEnumsAceGutachtenStatuscode.Abgegeben) {
        this.reviewDisabledDialog = this.dialogService.open({
          content: ConfirmationDialogComponent,
          maxWidth: Constants.DIALOG.MAX_WIDTH,
          maxHeight: Constants.DIALOG.MAX_HEIGHT,
          title: this.languageservice.instant("Enum.review.fristFuerBegutachtungEnded"),
        });

        let dialogComponent: ConfirmationDialogComponent = this.reviewDisabledDialog.content.instance;
        dialogComponent.content = "review.expireddialogue.content";
        dialogComponent.confirm = "review.expireddialogue.confirm";
        dialogComponent.showCancel = false;
      }

      this.reviewForm.Anfrage.disable({
        onlySelf: false
      });

      this.gesamtFormControl.setValue(this.gesamt);
    }

    get berechnungsmethode() {
        return this.reviewForm.Anfrage.FinanceData.Berechnungsmethode.value;
    }
    
    get reverseCalculationRow() {
        return this.berechnungsmethode == GlobalEnumsAceBerechnungsmethode.Gesamtprojektsumme;
    }

    public get rechtsformList(): EnumValue[] {
        let rechtsformList = GlobalEnumsAceRechtsform.getAsDataset();
        rechtsformList.forEach((rechtsform: EnumValue) => rechtsform.Description = this.languageservice.instant("Enum.GlobalEnumsAceRechtsform." + rechtsform.Name));
        return rechtsformList;
    }

    public getFieldLabelByBerechnungsmethode(label: string): string {
        if(GlobalEnumsAceBerechnungsmethode.Gesamtprojektsumme == this.berechnungsmethode) {
          if(label == 'finance-data.equitycapital')
            return 'finance-data.equitycapital.gesamtprojektsumme';
          if (label == 'finance-data.thirdPartyFunds')
            return 'finance-data.thirdPartyFunds.gesamtprojektsumme';
          
        }
    
        return label;
    }

    public get documents(): DocumentDisplayModel[] {
      return this.attachmentsFormControl.value;
    }

    public documentSize(document: DocumentDisplayModel): string {
      return document.fileSize ? ((document.fileSize / 1000000)).toFixed(2) + ' MB' : "";
    }

    public onDownloadDocumentClick(document: DocumentDisplayModel) {
      let subscription = this.gutachtenService.apiGutachtenGetDocumentGutachtenIdDocumentIdGet({ gutachtenId: this.review.gutachtenId!, documentId: document.documentId!, loadContent: true  })
      .subscribe(file => {
        if (file)
          this.documentService.download(file);
      });

      this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }

    public onDownloadDocumentsAsZipClick() {
      let subscription = this.gutachtenService.apiGutachtenGetDocumentsAsZipGutachtenIdGet({ gutachtenId: this.review.gutachtenId! })
      .subscribe(file => {
        if (file)
          this.documentService.download(file);
      });

      this.loadingAnimationService.showSpinnerUsingSubscription(subscription);
    }
}
